import { BLUE_100, BLUE_600, GRAY_100, GRAY_200, GRAY_300, GRAY_500, GRAY_600, GRAY_700, GRAY_800, GRAY_900, WHITE } from 'constants/styling/theme'
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material'
import { EditionAction, EditionType, PhotoStudioSidebarOption, generationTypeToEditionAction, useClientPhotoStudio } from './ClientPhotoStudio.context'
import { FC, Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PhotoStudioFlowType, RoomType } from 'constants/visualStaging/visualStagingTypes'

import { AIProcessingStep } from '../../common/useAIProgressMessages'
import { ActionPage } from 'components/common/ActionPage/ActionPage.component'
import { AdjustPanel } from './AdjustPanel'
import { BlurPanel } from './adjust/BlurPanel.component'
import BlurOnIcon from '@mui/icons-material/BlurOn'
import { AdjustStage } from './adjust/AdjustStage.module'
import { AnalyticsEvent } from 'utils/analytics'
import AutoFixHighOutlinedIcon from '@mui/icons-material/AutoFixHighOutlined'
import ChairOutlinedIcon from '@mui/icons-material/ChairOutlined'
import { ClientPhotoStudioFooter } from './ClientPhotoStudioFooter.module'
import { ClientPhotoStudioManualToolsPreview } from './ClientPhotoStudioManualToolsPreview.module'
import { EnhancePanel } from './EnhancePanel/EnhancePanel.component'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import { GenerationType } from 'constants/visualStaging/visualStagingTypes'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUITooltip } from 'components/common/MUITooltip'
import { MagicEraserPanel } from './MagicEraserPanel/MagicEraserPanel.component'
import PanoramaOutlinedIcon from '@mui/icons-material/PanoramaOutlined'
import PhotoFilterOutlinedIcon from '@mui/icons-material/PhotoFilterOutlined'
import { PhotoStudioGenerationStepId } from './generationSteps'
import { PhotoStudioHistorySource } from './hooks/usePhotoStudioHistory.hook'
import { PhotoStudioSaving } from '../../common/PhotoStudioSaving/PhotoStudioSaving.component'
import { ProcessingMode } from 'constants/visualStaging/visualStagingTypes'
import { SkyReplacementOption } from 'models/purchaseFlow/AIEditingDTO'
import { SvgIconProps } from '@mui/material/SvgIcon'
import { UnifiedPreview } from './UnifiedPreview'
import { VirtualStagingPanelContainer } from './VirtualStaging/components/VirtualStagingPanel'
import { VisualStagingSettings } from 'dataQueries/photoStudio.query'
import { WALL_COLORS } from './VirtualStaging/components/WallColorPicker/WallColorPicker.component'
import { WallPaintingSettings } from 'dataQueries/photoStudio.query'
import { getImageMetadata } from 'utils/serialization/getImageMetadata'
import { isEnhanceComingSoon } from './sidebarComingSoon'
import { logger } from 'utils/logging'
import { useAIEnhancementFlow } from './hooks/useAIEnhancementFlow.hook'
import { useButtonLogic } from './hooks/useButtonLogic.hook'
import { useDeclutterFlow } from './hooks/useDeclutterFlow.hook'
import { useFloorReplacementFlow } from './hooks/useFloorReplacementFlow.hook'
import { useGetAssignment } from 'dataQueries/assignment.query'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useVisualStagingActions } from './VisualStaging/hooks/useVisualStagingActions.hook'
import { useVisualStagingFlow } from './hooks/useVisualStagingFlow.hook'
import { useWallPaintingFlow } from './hooks/useWallPaintingFlow.hook'
import { useWorkspacePlan } from 'utils/hooks'
import {
  LAB_SDK_ENGINE,
  LabAllFeaturesPanel,
  LabCombinedSettings,
  LabEnhancePanel,
  LabEnhanceSettings,
  LabEraseSettings,
  LabLocalStage,
  LabPrivacyCategory,
  LabProgress,
  LabRunSource,
  LabSceneContent,
  LabStagePanel,
  LabStageSettings,
  LabToolsViewSwitch,
  buildLabCombinedOrderOptions,
  buildLabCombinedPlan,
  buildLabEnhanceOrderOptions,
  buildLabEraseOrderOptions,
  buildLabStageOrderOptions,
  buildLabStagePlan,
  resolveLabDefaultRunSource,
  resolveLabExportOptions,
  resolveLabRunSourceImage,
  resolveLabStageIndex,
  useLabGeneration,
  withLabPrepareStep
} from './lab'
import { ExpandedVisualType } from 'components/pages/Gallery/_main/ClientGallery/ClientGalleryPhoto'
import { toLabRecordedSettings } from './lab/labRecordedSettings'

/**
 * Maps PhotoStudioGenerationStepId (from flow/processor hooks) to AIProcessingStep (for progress messages)
 */
const mapProgressToAIStep = (step: string): AIProcessingStep => {
  switch (step) {
    case PhotoStudioGenerationStepId.UPLOADING_MASK:
    case PhotoStudioGenerationStepId.MASK_UPLOADED:
      return AIProcessingStep.UPLOADING_MASK
    case PhotoStudioGenerationStepId.START_PROCESSING:
    case PhotoStudioGenerationStepId.UPLOAD_MASK:
      return AIProcessingStep.PROCESSING
    case PhotoStudioGenerationStepId.PROCESSING_COMPLETE:
      return AIProcessingStep.FETCHING_RESULT
    case PhotoStudioGenerationStepId.FINALIZING:
      return AIProcessingStep.FINALIZING
    default:
      return AIProcessingStep.PROCESSING
  }
}

interface SidebarOptionConfig {
  id: PhotoStudioSidebarOption
  /** Translation key (`photo_studio_editing`) */
  labelKey: string
  Icon: React.ComponentType<SvgIconProps>
  hasDividerAfter?: boolean
}

// One tool view: icon-only tool tiles (the name shows on hover), so the options panel gets the width — the two columns
// still add up to the total
const TOTAL_PANEL_WIDTH = '44rem'
const LEFT_COLUMN_WIDTH = '7.2rem'
const RIGHT_COLUMN_WIDTH = '36.8rem'
const SIDEBAR_TILE_WIDTH = '4.8rem'
const SIDEBAR_TILE_HEIGHT = '4.8rem'

const SIDEBAR_OPTIONS: SidebarOptionConfig[] = [
  {
    id: PhotoStudioSidebarOption.STAGING,
    labelKey: 'sidebar_menu.staging',
    Icon: ChairOutlinedIcon,
  },
  {
    id: PhotoStudioSidebarOption.ENHANCE,
    labelKey: 'sidebar_menu.enhance',
    Icon: PhotoFilterOutlinedIcon,
  },
  {
    id: PhotoStudioSidebarOption.ERASER,
    labelKey: 'sidebar_menu.eraser',
    Icon: AutoFixHighOutlinedIcon,
    hasDividerAfter: true,
  },
  // In-house Adjust editor only (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT): with Pintura, Blur lives inside Adjust
  {
    id: PhotoStudioSidebarOption.BLUR,
    // Pintura's Blur label: the backend catalogue replaces the bundled `sidebar_menu` group whole (one-level merge), so a
    // key new there would render raw until the catalogue has it
    labelKey: 'manual_tools.BLUR',
    Icon: BlurOnIcon,
  },
  {
    id: PhotoStudioSidebarOption.ADJUST,
    labelKey: 'sidebar_menu.adjust',
    Icon: PanoramaOutlinedIcon,
  },
]

/** Which AI tool a Lab SDK order was started from — decides the history source, completion flag and analytics type. */
type LabRunKind = 'enhance' | 'stage' | 'erase'

const LAB_HISTORY_SOURCE: Record<LabRunKind, PhotoStudioHistorySource> = {
  enhance: PhotoStudioHistorySource.ENHANCE,
  stage: PhotoStudioHistorySource.VIRTUAL_STAGING,
  erase: PhotoStudioHistorySource.MAGIC_ERASER,
}

const LAB_EDITION_TYPE: Record<LabRunKind, EditionType> = {
  enhance: EditionType.ENHANCE,
  stage: EditionType.VIRTUAL_STAGING,
  erase: EditionType.MAGIC_ERASER,
}

/** What a Lab run records with its result (analytics): one tool's settings, or every tool of an all-features run */
type LabRunSettings = LabEnhanceSettings | LabStageSettings | LabEraseSettings | {
  features: LabRunKind[]
  stage?: LabStageSettings
  enhance?: LabEnhanceSettings
  erase?: LabEraseSettings
}

/** The privacy categories a run asked for: its Enhance's, alone or among All tools' */
const labPrivacyCategoriesOf = (settings?: LabRunSettings): LabPrivacyCategory[] => {
  const enhance = settings && ('privacyCategories' in settings ? settings : 'enhance' in settings ? settings.enhance : undefined)
  return enhance?.privacy ? enhance.privacyCategories : []
}

/**
 * A Lab run about to start. `kind` is the primary tool — the generative edit of an all-features run — and decides the
 * history source, the analytics edition type and the reveal; `kinds` lists every tool in the run (default: `kind`).
 */
interface LabRunStart {
  kind: LabRunKind
  kinds?: LabRunKind[]
  settings?: LabRunSettings
  plan: string[]
}

/**
 * Photo Studio controller for individual visual selected from Gallery/Property gallery
 * This component provides a user interface for editing images using AI and manual tools.
 * It allows users to select AI tools or manual tools for image editing, and provides a Pintura editor for advanced image manipulation.
 * @example <ClientPhotoStudioController />
 */
export const ClientPhotoStudioController: FC = () => {
  const { t } = useTranslation(['photo_studio_editing', 'common'])
  const { spawnErrorToast } = useSnackbar()
  const {
    previewImage,
    imageUrl,
    convertedImageUrl,
    sourceVisualFilename,
    assignmentId,
    handleUploadImage,
    handleProcessMagicEraser,
    isProcessingImage,
    setIsProcessingImage,
    setAIProcessingStep,
    isSaving,
    onExitStudio,
    selectedPinturaFeature,
    selectedAIFeature,
    logPhotoStudioEvent,
    setPreviewImage,
    isVirtualStaging,
    isMagicEraserTool,
    isAdjustTool,
    isBlurTool,
    isPinturaReplacement,
    hasAIEraserCompleted,
    onGenerateStarted,
    hasVisualStagingCompleted,
    setHasVisualStagingCompleted,
    hasEnhanceCompleted,
    setHasEnhanceCompleted,
    virtualStagingJobId,
    setVirtualStagingJobId,
    virtualStagingState,
    setVirtualStagingState,
    getLatestPreviewFilename,
    activeFeature,
    switchEditingMode,
    isToolSwitching,
    toolSwitchStep,
    toolSwitchProgress,
    getPreviewFileForAutoSave,
    uploadTemporaryVisualFile,
    setLatestPreviewFilename,
    hasMaskDrawnRef,
    maskAnalyticsRef,
    getLabDetectedSource,
    labEngine,
    uploadLabMask,
    isOriginalVisualType,
    imageGenerationSequenceNumber,
    setImageGenerationSequenceNumber,
    getCommonAnalyticsData,
    photoStudioHistory,
    isLabSdkEngine,
    isLabBeforeView,
    labWorkingImage,
    setExpandedVisualType,
    setLabProgress,
    setLabScene,
    labSceneImageRef,
    setLabStagePlan,
    setLabReveal,
    labRunSources,
    setHasAIEraserCompleted,
    isAllFeaturesView,
    isAllFeaturesViewAvailable,
  } = useClientPhotoStudio()

  // Get assignment data for analytics
  const assignmentQuery = useGetAssignment(assignmentId || '')
  const assignmentData = assignmentQuery.data?.data
  const propertyType = assignmentData && 'property' in assignmentData ? assignmentData.property?.propertyType : undefined

  // Track photo studio analytics PHOTO_STUDIO_OPENED on mount
  useEffect(() => {
    const trackPhotoStudioOpened = async () => {
      try {
        // Get image metadata
        const file = await fetch(imageUrl).then(res => res.blob()).then(blob => new File([blob], sourceVisualFilename || 'image.jpg', { type: blob.type }))
        const imageMetadata = await getImageMetadata(file)

        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_OPENED, {
          image_dimensions: `${imageMetadata.width}x${imageMetadata.height}`,
          image_format: file.type,
          propertyType: propertyType || undefined,
        })
      } catch (error) {
        // If metadata extraction fails, still log the event without metadata
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_OPENED, {
          propertyType: propertyType || undefined,
          image_format: 'Error extracting image format, photo studio still opened',
          image_dimensions: 'Error extracting image dimensions, photo studio still opened',
        })
      }
    }

    trackPhotoStudioOpened()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Only run on mount

  // Create refs before using them
  const generateRef = useRef<{ handleMagicEraserGenerate: () => Promise<void> }>(null)
  // The tools column is inert while a tool switch processes (no clicks, no keyboard focus); React 18 has no `inert` prop
  const leftColumnRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    leftColumnRef.current?.toggleAttribute('inert', isToolSwitching)
  }, [isToolSwitching])

  const { changeSelectedStyle } = useVisualStagingActions(virtualStagingState, setVirtualStagingState)

  // Track if room type has been selected for the first time in Visual Staging
  const hasRoomTypeBeenSelectedRef = useRef<boolean>(false)

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const allowAutoenhance = useFeatureFlag(FeatureFlag.ALLOW_AUTOENHANCE_PHOTO_STUDIO)
  const comingSoonSidebarOptionsSet = useMemo(() => {
    const options: PhotoStudioSidebarOption[] = []
    if (isEnhanceComingSoon({ allowAutoenhance, isLabSdkEngine })) {
      options.push(PhotoStudioSidebarOption.ENHANCE)
    }
    return new Set(options)
  }, [allowAutoenhance, isLabSdkEngine])

  const isEnhanceTool = useMemo(() => activeFeature === PhotoStudioSidebarOption.ENHANCE, [activeFeature])

  // Ref to track the last sky replacement option used for enhancement analytics
  const lastEnhanceSkyReplacementRef = useRef<SkyReplacementOption | null>(null)

  const [hasPendingMagicEraserChange, setHasPendingMagicEraserChange] = useState(false)
  const [hasPendingVisualStagingChange, setHasPendingVisualStagingChange] = useState(false)


  // Ref to always get the latest virtual staging state (prevents stale closures in memoized components)
  // This is needed because UnifiedPreview is memoized and doesn't re-render when callbacks change,
  // so getVisualStagingSettings must read from a ref to always return current roomType/style values
  const virtualStagingStateRef = useRef(virtualStagingState)
  useLayoutEffect(() => {
    virtualStagingStateRef.current = virtualStagingState
  }, [virtualStagingState])

  // Wrapper functions that update both state and ref synchronously
  // Reset room type selection tracking when switching away from Virtual Staging or Visual Staging
  useEffect(() => {
    if (!isVirtualStaging) {
      hasRoomTypeBeenSelectedRef.current = false
    }
  }, [isVirtualStaging])

  // Keep mask state in sync with active masking flows
  useEffect(() => {
    const isMaskingFlow = isMagicEraserTool || (
      isVirtualStaging && (
        virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE ||
        virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE
      )
    )
    if (!isMaskingFlow) {
      setHasMaskDrawn(false)
    }
  }, [isMagicEraserTool, isVirtualStaging, virtualStagingState.processingMode])

  // Track visual staging form changes to re-enable generate
  useEffect(() => {
    if (isVirtualStaging) {
      setHasPendingVisualStagingChange(true)
    }
  }, [isVirtualStaging, virtualStagingState.generationType, virtualStagingState.roomType, virtualStagingState.selectedStyle, virtualStagingState.processingMode, virtualStagingState.keepFloorUntouched, virtualStagingState.wallColor, virtualStagingState.floorTemplate])

  // State to trigger mask clearing after AI generation
  const [clearMaskTrigger, setClearMaskTrigger] = useState<number>(0)
  const [hasMaskDrawn, setHasMaskDrawn] = useState<boolean>(false)

  // Function to trigger mask clearing
  const triggerMaskClear = useCallback(() => {
    setHasMaskDrawn(false)
    setClearMaskTrigger(prev => prev + 1)
  }, [])

  // Determine current flow type
  const currentFlow = useMemo((): PhotoStudioFlowType => {
    // Virtual Staging (new) and Visual Staging (old) flows take priority
    if (isVirtualStaging) {
      if (virtualStagingState.processingMode === ProcessingMode.ENTIRE_IMAGE) {
        return PhotoStudioFlowType.VISUAL_STAGING_ENTIRE_IMAGE
      }
      if (virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE) {
        return PhotoStudioFlowType.VISUAL_STAGING_PRESERVE_ZONE
      }
      if (virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE) {
        return PhotoStudioFlowType.VISUAL_STAGING_REPLACE_ZONE
      }
    }

    // Visual Editing flows
    if (isMagicEraserTool) {
      return PhotoStudioFlowType.VISUAL_EDITING_MAGIC_ERASER
    }
    if (selectedPinturaFeature) {
      return PhotoStudioFlowType.VISUAL_EDITING_PINTURA
    }

    return PhotoStudioFlowType.VISUAL_EDITING_MAGIC_ERASER // Default fallback
  }, [isMagicEraserTool, selectedPinturaFeature, isVirtualStaging, virtualStagingState.processingMode])

  // Wrapper function that calls handleProcessMagicEraser and then triggers mask clear
  const handleProcessMagicEraserWithMaskClear = useCallback(async (maskedImageFile: File, onComplete?: (resultUrl: string) => void) => {

    try {
      await handleProcessMagicEraser(maskedImageFile, async (resultUrl: string) => {
        setPreviewImage(resultUrl)


        // Reset pending change flag and mask state only after successful generation completes
        // This ensures Generate button is disabled until user makes new changes
        setHasPendingMagicEraserChange(false)
        setHasMaskDrawn(false) // Clear mask state so Generate is disabled until new mask is drawn

        // Result becomes the new current version (preview is already updated)
        // We don't add it to history here - it will be saved before the next generation

        return resultUrl
      })

      // Call the completion callback with the generated result URL
      if (onComplete) {
        onComplete(previewImage)
      }
    } catch (error) {
      logger.error('Error in Magic Eraser generation', error, {
        context: {
          component: 'ClientPhotoStudioController',
          action: 'magicEraserGenerationError'
        }
      })
      // Revert to previous version on error
      photoStudioHistory.revertToPreviousVersion()
      throw error
    }
  }, [handleProcessMagicEraser, previewImage, setHasPendingMagicEraserChange, photoStudioHistory, setHasMaskDrawn, setPreviewImage])

  // Use refs to store the current values for useImperativeHandle
  const isMagicEraserToolRef = useRef(isMagicEraserTool)
  const onGenerateStartedRef = useRef(onGenerateStarted)
  const handleProcessMagicEraserRef = useRef(handleProcessMagicEraserWithMaskClear)

  isMagicEraserToolRef.current = isMagicEraserTool
  onGenerateStartedRef.current = onGenerateStarted
  handleProcessMagicEraserRef.current = handleProcessMagicEraserWithMaskClear

  // Use the extracted button logic hook
  const { canSave } = useButtonLogic({
    isProcessingImage,
    hasAIEraserCompleted,
    hasVisualStagingCompleted,
    hasEnhanceCompleted,
    selectedAIFeature,
  })
  // Unified preview ref
  const unifiedPreviewRef = useRef<{ getCurrentFlow: () => PhotoStudioFlowType; getPreviewImage: () => string }>(null)

  // Lab engine: the eraser's drawer follows the version the tools work on (Before ↔ After) and remounts on another image,
  // so a mask drawn on one never counts for the other
  useEffect(() => {
    if (!isLabSdkEngine) return
    setHasMaskDrawn(false)
    setHasPendingMagicEraserChange(false)
  }, [isLabSdkEngine, labWorkingImage, setHasMaskDrawn])

  // Handle mask changes
  const handleMaskChange = useCallback(() => {
    const maskPresent = hasMaskDrawnRef?.current ? hasMaskDrawnRef.current() : false
    setHasMaskDrawn(maskPresent)
    if (isMagicEraserTool) {
      setHasPendingMagicEraserChange(true)
    }
    if (isVirtualStaging) {
      setHasPendingVisualStagingChange(true)
    }
  }, [hasMaskDrawnRef, isMagicEraserTool, isVirtualStaging])

  // Handle preview image changes
  const handlePreviewImageChange = useCallback((imageUrl: string) => {
    // Update preview image when undo/redo changes it
    setPreviewImage(imageUrl)
  }, [setPreviewImage])

  // Ensure a temporary preview is saved for AI tools (useTempBucket=true) before generation
  const ensureTempPreviewForAITool = useCallback(async (): Promise<string> => {
    const previewFile = await getPreviewFileForAutoSave()
    const filename = await uploadTemporaryVisualFile(previewFile)
    setLatestPreviewFilename(filename)
    return filename
  }, [getPreviewFileForAutoSave, setLatestPreviewFilename, uploadTemporaryVisualFile])

  const isStagingMaskRequired = useMemo(() => (
    virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE ||
    virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE
  ), [virtualStagingState.processingMode])

  const canGenerateMagicEraser = useMemo(() => {
    // Magic Eraser can generate when there is a new mask-based change to process
    const hasMaskTrigger = hasMaskDrawn && hasPendingMagicEraserChange

    return hasMaskTrigger && !isProcessingImage && !isSaving && !isOriginalVisualType
  }, [hasMaskDrawn, hasPendingMagicEraserChange, isOriginalVisualType, isProcessingImage, isSaving])

  const canGenerateVirtualStaging = useMemo(() => {
    const isDeclutterMode = virtualStagingState.generationType === GenerationType.DECLUTTER_ROOM
    const isPaintWallsMode = virtualStagingState.generationType === GenerationType.PAINT_WALLS
    const isChangeFloorMode = virtualStagingState.generationType === GenerationType.CHANGE_FLOOR
    const hasRoomType = !!virtualStagingState.roomType
    const hasStyle = !!virtualStagingState.selectedStyle
    const hasWallColor = !!virtualStagingState.wallColor
    const hasFloorTemplate = !!virtualStagingState.floorTemplate
    const hasChanges = hasPendingVisualStagingChange
    const hasMaskGate = !isStagingMaskRequired || hasMaskDrawn

    // For declutter mode, always enabled (no selections required, no pending changes check)
    if (isDeclutterMode) {
      return !isProcessingImage && !isSaving && !isOriginalVisualType
    }
    // For paint walls mode, need wall color
    if (isPaintWallsMode) {
      return hasChanges && hasWallColor && !isProcessingImage && !isSaving && !isOriginalVisualType
    }
    // For change floor mode, need floor template
    if (isChangeFloorMode) {
      return hasChanges && hasFloorTemplate && !isProcessingImage && !isSaving && !isOriginalVisualType
    }
    // For other modes, require both room type and style
    return hasChanges && hasRoomType && hasStyle && hasMaskGate && !isProcessingImage && !isSaving && !isOriginalVisualType
  }, [hasMaskDrawn, hasPendingVisualStagingChange, isOriginalVisualType, isProcessingImage, isSaving, isStagingMaskRequired, virtualStagingState.generationType, virtualStagingState.roomType, virtualStagingState.selectedStyle, virtualStagingState.wallColor, virtualStagingState.floorTemplate])

  // Callback to map flow progress steps to AI processing steps for progress messages
  const handleProgressUpdate = useCallback((step: string) => {
    setAIProcessingStep(mapProgressToAIStep(step))
  }, [setAIProcessingStep])

  // Visual Staging flow (masked flows)
  const { uploadMaskAndProcess } = useVisualStagingFlow({
    assignmentId: assignmentId!,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onProgressUpdate: handleProgressUpdate,
    onComplete: async (resultUrl: string) => {

      setPreviewImage(resultUrl)

      setHasVisualStagingCompleted(true)
      setHasPendingVisualStagingChange(false)

      // Add new version to history with the result image
      const newSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      setImageGenerationSequenceNumber(newSequenceNumber)

      // Track visual generated
      const maskAnalytics = maskAnalyticsRef.current
      getCommonAnalyticsData(propertyType || undefined, virtualStagingState.roomType || undefined, newSequenceNumber).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionAction: generationTypeToEditionAction(virtualStagingState.generationType),
          editionSettings: {
            processingMode: virtualStagingState.processingMode,
            roomType: virtualStagingState.roomType,
            selectedStyle: virtualStagingState.selectedStyle,
            generationType: virtualStagingState.generationType,
            keepFloorUntouched: virtualStagingState.keepFloorUntouched,
            ...(maskAnalytics.numberOfMasksApplied > 0 && {
              brushSize: maskAnalytics.brushSize,
              numberOfMasksApplied: maskAnalytics.numberOfMasksApplied,
            }),
          },
          ...commonData,
        })
      })

      await photoStudioHistory.addVersion(resultUrl, PhotoStudioHistorySource.VIRTUAL_STAGING, newSequenceNumber, {
        processingMode: virtualStagingState.processingMode,
        roomType: virtualStagingState.roomType,
        selectedStyle: virtualStagingState.selectedStyle,
        generationType: virtualStagingState.generationType,
        keepFloorUntouched: virtualStagingState.keepFloorUntouched,
        ...(maskAnalytics.numberOfMasksApplied > 0 && {
          brushSize: maskAnalytics.brushSize,
          numberOfMasksApplied: maskAnalytics.numberOfMasksApplied,
        }),
      })
    },
    onError: (error: any) => {
      logger.error('Virtual Staging error', error)
      const displayErrorMessage = t('virtual_staging_error')
      spawnErrorToast(displayErrorMessage)
      setIsProcessingImage(false)
    }
  })

  // Floor Replacement flow
  const { processFloorReplacementWithTemplate } = useFloorReplacementFlow({
    assignmentId: assignmentId!,
    onProgressUpdate: handleProgressUpdate,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onComplete: async (resultUrl: string) => {

      setPreviewImage(resultUrl)


      // Mark Visual Staging as completed for Save button
      setHasVisualStagingCompleted(true)
      setIsProcessingImage(false)

      // Track visual generated
      const newSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      setImageGenerationSequenceNumber(newSequenceNumber)
      getCommonAnalyticsData(propertyType || undefined, virtualStagingState.roomType || undefined, newSequenceNumber).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionAction: EditionAction.CHANGE_FLOOR,
          editionSettings: {
            generationType: virtualStagingState.generationType,
            floorTemplate: virtualStagingState.floorTemplate,
          },
          ...commonData,
        })
      })

      // Add new version to history with the result image
      await photoStudioHistory.addVersion(resultUrl, PhotoStudioHistorySource.VIRTUAL_STAGING, newSequenceNumber, {
        generationType: virtualStagingState.generationType,
        floorTemplate: virtualStagingState.floorTemplate,
      })

      // Reset pending change flag
      setHasPendingVisualStagingChange(false)
    },
    onError: (error: any) => {
      logger.error('Floor Replacement error', error, {
        context: { component: 'ClientPhotoStudioController', action: 'floorReplacementError' }
      })
      // Revert to previous version on error
      photoStudioHistory.revertToPreviousVersion()
      const errorMessage = error?.response?.data?.message || error?.message
      if (errorMessage?.includes('Missing visual filename')) {
        const toastMessage = t('photo_studio_editing:error_missing_visual_or_job_id_toast')
        spawnErrorToast(toastMessage)
        setIsProcessingImage(false)
        return
      }

      // Mirror Magic Eraser error behavior on preview
      const displayErrorMessage = t('general_error', { error: errorMessage || t('unknown_error') })
      spawnErrorToast(displayErrorMessage)
      setIsProcessingImage(false)
    }
  })

  // Wall Painting flow
  const { processWallPaintingWithSettings } = useWallPaintingFlow({
    assignmentId: assignmentId!,
    onProgressUpdate: handleProgressUpdate,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onComplete: async (resultUrl: string) => {

      setPreviewImage(resultUrl)


      // Mark Visual Staging as completed for Save button
      setHasVisualStagingCompleted(true)
      setIsProcessingImage(false)

      // Track visual generated
      const newSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      setImageGenerationSequenceNumber(newSequenceNumber)
      getCommonAnalyticsData(propertyType || undefined, virtualStagingState.roomType || undefined, newSequenceNumber).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionAction: generationTypeToEditionAction(virtualStagingState.generationType),
          editionSettings: {
            generationType: virtualStagingState.generationType,
            wallColor: virtualStagingState.wallColor,
          },
          ...commonData,
        })
      })

      // Add new version to history with the result image
      await photoStudioHistory.addVersion(resultUrl, PhotoStudioHistorySource.VIRTUAL_STAGING, newSequenceNumber, {
        generationType: virtualStagingState.generationType,
        wallColor: virtualStagingState.wallColor,
      })

      // Reset pending change flag and mask state only after successful generation completes
      // This ensures Generate button is disabled until user makes new changes
      setHasPendingVisualStagingChange(false)
      // Clear mask state for masked flows (preserve/replace zone) so Generate is disabled until new mask is drawn
      if (virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE ||
        virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE) {
        setHasMaskDrawn(false)
      }
    },
    onError: (error: any) => {
      logger.error('Wall Painting error', error, {
        context: { component: 'ClientPhotoStudioController', action: 'wallPaintingError' }
      })
      // Revert to previous version on error
      photoStudioHistory.revertToPreviousVersion()
      // Check if this is the validation error for missing visual filename or job ID
      const errorMessage = (error as Error)?.message
      if (errorMessage === 'photo_studio_editing.error_missing_visual_or_job_id') {
        const toastMessage = t('photo_studio_editing:error_missing_visual_or_job_id_toast')
        spawnErrorToast(toastMessage)
        setIsProcessingImage(false)
        return
      }

      // Mirror Magic Eraser error behavior on preview
      const displayErrorMessage = t('general_error', { error: errorMessage || t('unknown_error') })
      spawnErrorToast(displayErrorMessage)
      setIsProcessingImage(false)
    }
  })

  // Declutter flow
  const { processDeclutterRoom } = useDeclutterFlow({
    assignmentId: assignmentId!,
    onProgressUpdate: handleProgressUpdate,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onComplete: async (resultUrl: string) => {

      setPreviewImage(resultUrl)


      // Mark Visual Staging as completed for Save button
      setHasVisualStagingCompleted(true)
      setIsProcessingImage(false)

      // Track visual generated
      const newSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      setImageGenerationSequenceNumber(newSequenceNumber)
      getCommonAnalyticsData(propertyType || undefined, virtualStagingState.roomType || undefined, newSequenceNumber).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
          editionType: EditionType.VIRTUAL_STAGING,
          editionAction: EditionAction.DECLUTTER_ROOM,
          editionSettings: {
            generationType: GenerationType.DECLUTTER_ROOM,
          },
          ...commonData,
        })
      })

      // Add new version to history with the result image
      await photoStudioHistory.addVersion(resultUrl, PhotoStudioHistorySource.VIRTUAL_STAGING, newSequenceNumber, {
        generationType: GenerationType.DECLUTTER_ROOM,
      })

      // NOTE: Don't reset hasPendingVisualStagingChange for declutter mode
      // since canGenerateVirtualStaging doesn't check it for declutter mode
      // This allows users to regenerate without making changes
    },
    onError: (error: any) => {
      logger.error('Declutter error', error, {
        context: { component: 'ClientPhotoStudioController', action: 'declutterError' }
      })
      // Revert to previous version on error
      photoStudioHistory.revertToPreviousVersion()
      const errorMessage = error?.response?.data?.message || error?.message
      if (errorMessage?.includes('Missing visual filename')) {
        const toastMessage = t('photo_studio_editing:error_missing_visual_or_job_id_toast')
        spawnErrorToast(toastMessage)
        setIsProcessingImage(false)
        return
      }

      // Mirror Magic Eraser error behavior on preview
      const displayErrorMessage = t('general_error', { error: errorMessage || t('unknown_error') })
      spawnErrorToast(displayErrorMessage)
      setIsProcessingImage(false)
    }
  })

  // AI Enhancement flow
  const { processAIEnhancement } = useAIEnhancementFlow({
    assignmentId: assignmentId!,
    onProgressUpdate: handleProgressUpdate,
    sourceVisualFilename,
    getLatestPreviewFilename,
    onComplete: async (resultUrl: string) => {
      setPreviewImage(resultUrl)
      setIsProcessingImage(false)
      setHasEnhanceCompleted(true)

      // Track visual generated
      const newSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      setImageGenerationSequenceNumber(newSequenceNumber)
      const currentSkyReplacement = lastEnhanceSkyReplacementRef.current
      getCommonAnalyticsData(propertyType || undefined, undefined, newSequenceNumber).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
          editionType: EditionType.ENHANCE,
          editionAction: EditionAction.ENHANCE,
          editionSettings: {
            skyReplacement: currentSkyReplacement || null,
          },
          ...commonData,
        })
      })

      // Add new version to history with the result image
      await photoStudioHistory.addVersion(resultUrl, PhotoStudioHistorySource.ENHANCE, newSequenceNumber, {
        skyReplacement: currentSkyReplacement || null,
      })
    },
    onError: (error: any) => {
      logger.error('AI Enhancement error', error, {
        context: { component: 'ClientPhotoStudioController', action: 'aiEnhancementError' }
      })
      // Revert to previous version on error
      photoStudioHistory.revertToPreviousVersion()
      const errorMessage = error?.response?.data?.message || error?.message
      if (errorMessage?.includes('Missing visual filename')) {
        const toastMessage = t('photo_studio_editing:error_missing_visual_or_job_id_toast')
        spawnErrorToast(toastMessage)
        setIsProcessingImage(false)
        return
      }

      const displayErrorMessage = t('general_error', { error: errorMessage || t('unknown_error') })
      spawnErrorToast(displayErrorMessage)
      setIsProcessingImage(false)
    }
  })

  const getVisualStagingSettings = useCallback((): VisualStagingSettings => {
    // Read from ref to always get the latest state (prevents stale closures
    // when called from memoized UnifiedPreview that skips callback re-renders)
    const state = virtualStagingStateRef.current

    // Map KID_BEDROOM to BEDROOM for backend compatibility
    const backendRoomType = state.roomType === RoomType.KID_BEDROOM
      ? RoomType.BEDROOM
      : state.roomType

    return {
      generationType: state.generationType,
      style: state.selectedStyle as unknown as string,
      processingMode: state.processingMode,
      roomType: backendRoomType as unknown as string,
      keepFloor: state.keepFloorUntouched
    }
  }, [])

  const getWallPaintingSettings = useCallback((): WallPaintingSettings => {
    // Read from ref to always get the latest state (same pattern as getVisualStagingSettings)
    const state = virtualStagingStateRef.current

    // Get the selected color or default to first color
    const selectedColorId = state.wallColor || WALL_COLORS[0].id
    const selectedColor = WALL_COLORS.find(c => c.id === selectedColorId) || WALL_COLORS[0]

    // Map color ID to API format (using API-friendly color name with spaces)
    // The API expects: { wall: string, ceiling: string, door: string, floorFrame: string }
    // Use selected color for wall, and 'white' for all other surfaces, same as Propershot does in UI
    return {
      colors: {
        wall: selectedColor.apiName,
        ceiling: 'white',
        door: 'white',
        floorFrame: 'white',
      }
    }
  }, [])


  // ---------------------------------------------------------------------------------------------------
  // Lab engine (FeatureFlag.ALLOW_PHOTO_STUDIO_LAB_SDK), run by the platform: upload → start the tool → follow its
  // job → download. The result then joins the same preview / history / save flow as the ai-visuals engine.
  // ---------------------------------------------------------------------------------------------------
  /** The run going on */
  const labRunRef = useRef<{ kind: LabRunKind, kinds: LabRunKind[], settings?: LabRunSettings, plan: string[], stageIndex: number, percent: number, source: LabRunSource, sourceImageUrl: string } | null>(null)
  /** An all-features run with Erase: the preview exports the mask, then calls handleGenerate back with it */
  const pendingLabAllFeaturesEraseRef = useRef<LabCombinedSettings | null>(null)

  /** Settings recorded with a Lab result — in its history entry (save analytics) and on VISUAL_GENERATED */
  const labEditionSettings = (jobId: string): Record<string, unknown> => {
    const run = labRunRef.current
    return {
      engine: LAB_SDK_ENGINE,
      jobId,
      runSource: run?.source ?? LabRunSource.AFTER,
      // A described style is the user's own words: history and analytics keep its length only
      ...toLabRecordedSettings({ ...(run?.settings ?? {}) }),
      // Same action as the ai-visuals eraser records
      ...(run?.kind === 'erase' ? { editionAction: EditionAction.ERASE } : {}),
    }
  }

  /**
   * Normalises the Lab's progress before it reaches the UI: the free-form label (`sampling 5/8`, `fal-1`)
   * is resolved onto the run's stage plan and neither the stage nor the percentage may move backwards,
   * so the overlay's bar and message always agree and never jump back to "Develop".
   */
  const handleLabProgress = useCallback((progress: LabProgress | null) => {
    const run = labRunRef.current
    // The run is over (result or failure): its last scene must not replay on the next one
    if (!progress) setLabScene(null)
    if (!progress || !run || run.plan.length === 0) {
      setLabProgress(progress)
      return
    }
    run.stageIndex = Math.max(run.stageIndex, resolveLabStageIndex(progress.stage, run.plan, progress.percent))
    run.percent = Math.max(run.percent, progress.percent)
    setLabProgress({ ...progress, rawStage: progress.stage, stage: run.plan[run.stageIndex], percent: run.percent })
  }, [setLabProgress, setLabScene])

  /** Each scene gets the next id, so the preview stages every one of them, in order, even two alike */
  const labSceneIdRef = useRef(0)
  const handleLabScene = useCallback((scene: LabSceneContent) => {
    labSceneIdRef.current += 1
    const run = labRunRef.current
    // A detection of several prompts names none: it is a blend of what the run asked for
    const content = scene.kind === 'detect' && scene.categories.length === 0 ? { ...scene, categories: labPrivacyCategoriesOf(run?.settings) } : scene
    setLabScene({ ...content, id: labSceneIdRef.current })
  }, [setLabScene])

  const { runLabOrder } = useLabGeneration({
    engine: labEngine,
    getSourceFile: getPreviewFileForAutoSave,
    uploadVisual: uploadTemporaryVisualFile,
    uploadMask: uploadLabMask,
    onProgress: handleLabProgress,
    onScene: handleLabScene,
    commitResult: (resultUrl, { jobId, signal }) => {
      const run = labRunRef.current
      const kind = run?.kind ?? 'enhance'
      const sequence = (imageGenerationSequenceNumber || 0) + 1
      return photoStudioHistory.addVersion(resultUrl, LAB_HISTORY_SOURCE[kind], sequence, labEditionSettings(jobId), run?.sourceImageUrl ?? previewImage, signal)
    },
    onComplete: (resultUrl, { jobId }) => {
      const run = labRunRef.current
      const kind: LabRunKind = run?.kind ?? 'enhance'
      const kinds = run?.kinds ?? [kind]
      // The result is preloaded by the hook; reveal it over the image it replaces (top-to-bottom wipe). Not after an
      // erase: its After is the mask drawer, which has no wipe, and a pending one would replay on the next Compare
      // From the image on screen at the end of the run: the latest step image the scenes crossfaded in over the photo
      const revealFrom = labSceneImageRef.current ?? previewImage
      if (kind !== 'erase' && revealFrom && revealFrom !== resultUrl) setLabReveal({ from: revealFrom, to: resultUrl })
      setIsProcessingImage(false)

      if (kinds.includes('enhance')) setHasEnhanceCompleted(true)
      if (kinds.includes('stage')) {
        setHasVisualStagingCompleted(true)
        setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
      }
      if (kinds.includes('erase')) {
        setHasAIEraserCompleted(true)
        setHasPendingMagicEraserChange(false)
        setHasMaskDrawn(false)
        setClearMaskTrigger(prev => prev + 1)
      }

      const newSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      setImageGenerationSequenceNumber(newSequenceNumber)
      const editionSettings = labEditionSettings(jobId)
      getCommonAnalyticsData(propertyType || undefined, undefined, newSequenceNumber).then(commonData => {
        logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_VISUAL_GENERATED, {
          editionType: LAB_EDITION_TYPE[kind],
          ...(kind === 'erase' ? { editionAction: EditionAction.ERASE } : {}),
          engine: LAB_SDK_ENGINE,
          editionSettings,
          ...commonData,
        })
      })
    },
    onError: (error) => {
      // Back to the version on stage before the run — the preview may be showing the Before / Original the run consumed
      const shownBeforeRun = photoStudioHistory.historyState.current
      if (shownBeforeRun) setPreviewImage(shownBeforeRun.imageUrl)
      setIsProcessingImage(false)
      setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
      const message = error instanceof Error ? error.message : String(error)
      spawnErrorToast(t('lab.error', { error: message }))
    },
  })

  /**
   * Common start of every Lab run: remember the kind and source, show the overlay, snapshot the current version for
   * undo, and put a Before / Original pick on stage so the result reveals over the image it was generated from.
   * Returns the URL of the image the order runs on.
   */
  const startLabRun = useCallback(async ({ kind, kinds = [kind], settings, plan: orderPlan }: LabRunStart, pickedSource?: LabRunSource, signal?: AbortSignal): Promise<string> => {
    // No pick from the chevron: the version on screen — the Before view reuses that Before (the CTA hides the chevron)
    const source = resolveLabDefaultRunSource(pickedSource, isLabBeforeView)
    const sourceImageUrl = resolveLabRunSourceImage(labRunSources, source)
    // The run plays on the stage like any other: overlay over its source, then the result revealed (After)
    if (isLabBeforeView) setExpandedVisualType(ExpandedVisualType.EDITED)
    // The bar leads with "Prepare": snapshot, uploads and queue are named from the click on, not read as the first stage at 0%
    const plan = withLabPrepareStep(orderPlan)
    labRunRef.current = { kind, kinds, settings, plan, stageIndex: 0, percent: 0, source, sourceImageUrl }
    labSceneImageRef.current = null
    setLabStagePlan(plan)
    handleLabProgress({ stage: LabLocalStage.PREPARE, percent: 0 })
    setIsProcessingImage(true)
    if (kinds.includes('enhance')) setHasEnhanceCompleted(false)
    if (kinds.includes('stage')) {
      setHasVisualStagingCompleted(false)
      setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))
    }
    if (previewImage) {
      const nextSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
      await photoStudioHistory.addVersion(previewImage, LAB_HISTORY_SOURCE[kind], nextSequenceNumber, undefined, undefined, signal)
    }
    signal?.throwIfAborted()
    if (sourceImageUrl !== previewImage) setPreviewImage(sourceImageUrl)
    return sourceImageUrl
  }, [handleLabProgress, imageGenerationSequenceNumber, isLabBeforeView, labRunSources, labSceneImageRef, photoStudioHistory, previewImage, setExpandedVisualType, setHasEnhanceCompleted, setHasVisualStagingCompleted, setIsProcessingImage, setLabStagePlan, setPreviewImage, setVirtualStagingState])

  const handleLabEnhanceGenerate = useCallback(async (settings: LabEnhanceSettings, source?: LabRunSource) => {
    await runLabOrder({
      buildOrder: (inputAssetId) => buildLabEnhanceOrderOptions(inputAssetId, settings),
      exportOptions: resolveLabExportOptions(settings),
      prepareSource: (signal) => startLabRun({ kind: 'enhance', settings, plan: buildLabStagePlan('enhance', settings) }, source, signal),
      action: 'labEnhance',
    })
  }, [runLabOrder, startLabRun])

  const handleLabStageGenerate = useCallback(async (settings: LabStageSettings, source?: LabRunSource) => {
    await runLabOrder({
      buildOrder: (inputAssetId) => buildLabStageOrderOptions(inputAssetId, settings),
      exportOptions: resolveLabExportOptions(),
      prepareSource: (signal) => startLabRun({ kind: 'stage', settings, plan: buildLabStagePlan('stage', settings) }, source, signal),
      action: 'labStage',
    })
  }, [runLabOrder, startLabRun])

  // The eraser mask is drawn over the shown image, so an erase always runs on it (no run-source menu)
  const runLabErase = useCallback(async (maskFile: File) => {
    // Read now: the mask (and its counters) is cleared before the result is recorded
    const { brushSize, numberOfMasksApplied } = maskAnalyticsRef.current
    const settings: LabEraseSettings = { brushSize, numberOfMasks: numberOfMasksApplied }
    await runLabOrder({
      buildOrder: (inputAssetId, maskAssetId) => buildLabEraseOrderOptions(inputAssetId, maskAssetId ?? ''),
      exportOptions: resolveLabExportOptions(),
      maskFile,
      prepareSource: (signal) => startLabRun({ kind: 'erase', settings, plan: buildLabStagePlan('erase') }, undefined, signal),
      // The photo on stage is already uploaded when its objects were detected
      detectedSource: getLabDetectedSource,
      action: 'labErase',
    })
  }, [getLabDetectedSource, maskAnalyticsRef, runLabOrder, startLabRun])

  /** "Display all features at once": every section that is on, in one Lab order (`maskFile` when Erase is on) */
  const runLabAllFeatures = useCallback(async (settings: LabCombinedSettings, source?: LabRunSource, maskFile?: File) => {
    const generative = settings.generative
    const kinds: LabRunKind[] = [...(generative ? [generative.kind] : []), ...(settings.enhance ? ['enhance' as const] : [])]
    const isErase = generative?.kind === 'erase'
    // Read now: the mask (and its counters) is cleared before the result is recorded
    const { brushSize, numberOfMasksApplied } = maskAnalyticsRef.current
    const runSettings: LabRunSettings = {
      features: kinds,
      ...(generative?.kind === 'stage' ? { stage: generative.settings } : {}),
      ...(settings.enhance ? { enhance: settings.enhance } : {}),
      ...(isErase ? { erase: { brushSize, numberOfMasks: numberOfMasksApplied } } : {}),
    }
    await runLabOrder({
      buildOrder: (inputAssetId, maskAssetId) => buildLabCombinedOrderOptions(inputAssetId, settings, maskAssetId),
      exportOptions: resolveLabExportOptions(settings.enhance),
      maskFile,
      // The eraser's mask is drawn over the shown image, so a run with Erase always starts from it
      prepareSource: (signal) => startLabRun({ kind: kinds[0] ?? 'enhance', kinds, settings: runSettings, plan: buildLabCombinedPlan(settings) }, isErase ? undefined : source, signal),
      detectedSource: isErase ? getLabDetectedSource : undefined,
      action: 'labAllFeatures',
    })
  }, [getLabDetectedSource, maskAnalyticsRef, runLabOrder, startLabRun])

  const handleLabAllFeaturesGenerate = useCallback(async (settings: LabCombinedSettings, source?: LabRunSource) => {
    if (settings.generative?.kind === 'erase') {
      // The mask lives in the preview's drawer: it exports it and calls handleGenerate back with the file, which
      // consumes the pending settings synchronously — cleared anyway so a failed export never leaks into a later erase
      pendingLabAllFeaturesEraseRef.current = settings
      try {
        await generateRef.current?.handleMagicEraserGenerate()
      } finally {
        pendingLabAllFeaturesEraseRef.current = null
      }
      return
    }
    await runLabAllFeatures(settings, source)
  }, [runLabAllFeatures])

  // Handle generation
  const handleGenerate = useCallback((maskedImageFile?: File, skyReplacement?: SkyReplacementOption | null) => {
    const runGenerate = async () => {
      // Lab SDK engine: the eraser mask comes through this path (drawn in UnifiedPreview) — Enhance and
      // Stage have their own handlers wired to the Lab panels.
      if (isLabSdkEngine && isMagicEraserTool) {
        // "Display all features at once" with Erase on: the rest of the run rides along with the mask
        const allFeaturesSettings = pendingLabAllFeaturesEraseRef.current
        pendingLabAllFeaturesEraseRef.current = null
        if (maskedImageFile) {
          if (allFeaturesSettings) await runLabAllFeatures(allFeaturesSettings, undefined, maskedImageFile)
          else await runLabErase(maskedImageFile)
          return
        }
        // Never a silent dead button: say what is missing, and keep the dropped click diagnosable
        logger.warn('Lab SDK: eraser generate without a mask', null, {
          context: { component: 'PhotoStudio', action: 'labErase', metadata: { engine: LAB_SDK_ENGINE, hasMaskDrawn } },
        })
        spawnErrorToast(t('disabled_generate_button'))
        return
      }

      setAIProcessingStep(AIProcessingStep.STARTING)
      const shouldShowGeneratingOverlay = isVirtualStaging || isMagicEraserTool

      // Get the current generationType to ensure we have the latest value
      // This is important because state updates are async and we want to use the most recent state
      let currentGenerationType = virtualStagingState.generationType

      // Save current version BEFORE generation starts (so we can undo back to it)
      // Use the sequence number that will be set after generation completes
      if (shouldShowGeneratingOverlay && previewImage) {
        const nextSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
        await photoStudioHistory.addVersion(previewImage, isMagicEraserTool ? PhotoStudioHistorySource.MAGIC_ERASER : PhotoStudioHistorySource.VIRTUAL_STAGING, nextSequenceNumber)
      }

      // Always ensure temp preview exists for AI flows using temp bucket
      if (shouldShowGeneratingOverlay) {
        if (!isProcessingImage) {
          setIsProcessingImage(true)
        }
        // Set isGeneratingStaging immediately for virtual staging to disable submenu at the same time as main menu
        if (isVirtualStaging && !virtualStagingState.isGeneratingStaging) {
          setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))
        }
        try {
          await ensureTempPreviewForAITool()
          // History is committed in onComplete callback after processing succeeds
        } catch (error) {
          spawnErrorToast(t('general_error', { error }))
          setIsProcessingImage(false)
          setAIProcessingStep(undefined)
          // Reset isGeneratingStaging on error
          if (isVirtualStaging) {
            setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
          }
          return
        }
      }

      // If Magic Eraser with no mask and no prior AI result, stop and reset loading
      // Only check hasMaskDrawn state if maskedImageFile is not provided (maskedImageFile is the source of truth)
      if (isMagicEraserTool && !maskedImageFile && !hasMaskDrawn && !hasAIEraserCompleted) {
        if (shouldShowGeneratingOverlay) {
          setIsProcessingImage(false)
          setAIProcessingStep(undefined)
        }
        return
      }

      // Check generationType FIRST to route to the correct endpoint
      // DECLUTTER_ROOM, PAINT_WALLS, and CHANGE_FLOOR have their own endpoints and should be handled first
      if (isVirtualStaging && currentGenerationType === GenerationType.DECLUTTER_ROOM) {
        // Declutter flow - no mask/settings needed, process directly
        // Reset completion state when starting a new generation
        setHasVisualStagingCompleted(false)
        setIsProcessingImage(true)
        setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))
        processDeclutterRoom(virtualStagingJobId || undefined)
          .then((jobId) => {
            // Store the Declutter job ID for save functionality
            setVirtualStagingJobId(jobId)
            // NOTE: Don't reset hasPendingVisualStagingChange - not needed for declutter
            // since canGenerateVirtualStaging doesn't check it for declutter mode
          })
          .catch((error) => {
            logger.error('Declutter: Pre-processing or generation failed', error, {
              context: { component: 'PhotoStudio', action: 'declutterFlow' }
            })
            photoStudioHistory.revertToPreviousVersion()
          })
          .finally(() => {
            setIsProcessingImage(false)
            setAIProcessingStep(undefined)
            setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
          })
      } else if (isVirtualStaging && currentGenerationType === GenerationType.PAINT_WALLS) {
        // Wall Painting flow - no mask needed, process directly
        const settings = getWallPaintingSettings()
        // Reset completion state when starting a new generation
        setHasVisualStagingCompleted(false)
        setIsProcessingImage(true)
        setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))
        processWallPaintingWithSettings(settings, virtualStagingJobId || undefined)
          .then((jobId) => {
            // Store the Wall Painting job ID for save functionality
            setVirtualStagingJobId(jobId)
            // Don't reset hasPendingVisualStagingChange here - it will be reset in onComplete after result is ready
          })
          .catch((error) => {
            logger.error('Wall Painting: Pre-processing or generation failed', error, {
              context: { component: 'PhotoStudio', action: 'wallPaintingFlow' }
            })
            photoStudioHistory.revertToPreviousVersion()
          })
          .finally(() => {
            setIsProcessingImage(false)
            setAIProcessingStep(undefined)
            setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
          })
      } else if (isVirtualStaging && currentGenerationType === GenerationType.CHANGE_FLOOR) {
        // Floor Replacement flow - no mask needed, process directly
        const floorTemplate = virtualStagingState.floorTemplate || 'hardwood_aria' // Default to first template
        // Reset completion state when starting a new generation
        setHasVisualStagingCompleted(false)
        setIsProcessingImage(true)
        setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))
        processFloorReplacementWithTemplate(floorTemplate, virtualStagingJobId || undefined)
          .then((jobId) => {
            // Store the Floor Replacement job ID for save
            setVirtualStagingJobId(jobId)
            // Don't reset hasPendingVisualStagingChange here - it will be reset in onComplete after result is ready
          })
          .catch((error) => {
            logger.error('Floor Replacement: Pre-processing or generation failed', error, {
              context: { component: 'PhotoStudio', action: 'floorReplacementFlow' }
            })
            photoStudioHistory.revertToPreviousVersion()
          })
          .finally(() => {
            setIsProcessingImage(false)
            setAIProcessingStep(undefined)
            setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
          })
      } else if (maskedImageFile) {
        // Route masked flows: use Visual Staging endpoint for Virtual/Visual Staging, Magic Eraser otherwise
        if (isVirtualStaging) {
          // Only use mask if processing mode requires it (PRESERVE_ZONE or REPLACE_ZONE)
          // For ENTIRE_IMAGE mode, ignore the mask and process without it
          const requiresMask = virtualStagingState.processingMode === ProcessingMode.PRESERVE_ZONE ||
            virtualStagingState.processingMode === ProcessingMode.REPLACE_ZONE

          const settings = getVisualStagingSettings()
          // Reset completion state when starting a new generation
          setHasVisualStagingCompleted(false)
          // Mirror Magic Eraser loading overlay and steps
          setIsProcessingImage(true)
          setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))

          // Only pass mask if processing mode requires it
          const maskFileToUse = requiresMask ? maskedImageFile : undefined
          uploadMaskAndProcess(maskFileToUse, settings, virtualStagingJobId || undefined)
            .then((jobId) => {
              // Store the Visual Staging job ID for save functionality
              setVirtualStagingJobId(jobId)
              // Don't reset hasPendingVisualStagingChange here - it will be reset in onComplete after result is ready
            })
            .catch((error) => {
              logger.error('Visual Staging: Pre-processing or generation failed', error, {
                context: { component: 'PhotoStudio', action: 'visualStagingFlow' }
              })
              photoStudioHistory.revertToPreviousVersion()
            })
            .finally(() => {
              setIsProcessingImage(false)
              setAIProcessingStep(undefined)
              setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
            })
        } else {
          // Magic Eraser flow - history already started in handleGenerate
          handleProcessMagicEraserWithMaskClear(maskedImageFile)
          // Don't reset hasPendingMagicEraserChange here - it will be reset after successful generation completes
        }
      } else if (isVirtualStaging && virtualStagingState.processingMode === ProcessingMode.ENTIRE_IMAGE) {
        // Visual Staging entire image flow - no mask needed, process directly
        const settings = getVisualStagingSettings()
        // Reset completion state when starting a new generation
        setHasVisualStagingCompleted(false)
        setIsProcessingImage(true)
        setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: true }))
        uploadMaskAndProcess(undefined, settings, virtualStagingJobId || undefined)
          .then((jobId) => {
            // Store the Visual Staging job ID for save functionality
            setVirtualStagingJobId(jobId)
            // Don't reset hasPendingVisualStagingChange here - it will be reset in onComplete after result is ready
          })
          .catch((error) => {
            logger.error('Visual Staging (entire image): Pre-processing or generation failed', error, {
              context: { component: 'PhotoStudio', action: 'visualStagingEntireImageFlow' }
            })
            photoStudioHistory.revertToPreviousVersion()
          })
          .finally(() => {
            setIsProcessingImage(false)
            setAIProcessingStep(undefined)
            setVirtualStagingState(prev => ({ ...prev, isGeneratingStaging: false }))
          })
      } else if (isEnhanceTool) {
        // AI Enhancement flow - no mask needed, process directly
        setIsProcessingImage(true)
        setHasEnhanceCompleted(false)
        lastEnhanceSkyReplacementRef.current = skyReplacement as SkyReplacementOption | null

        // Save current version BEFORE generation starts (so we can undo back to it)
        if (previewImage) {
          const nextSequenceNumber = (imageGenerationSequenceNumber || 0) + 1
          await photoStudioHistory.addVersion(previewImage, PhotoStudioHistorySource.ENHANCE, nextSequenceNumber)
        }

        ensureTempPreviewForAITool()
          .then(() => processAIEnhancement(skyReplacement))
          .catch((error) => {
            logger.error('AI Enhancement: Pre-processing or enhancement failed', error, {
              context: {
                component: 'PhotoStudio',
                action: 'aiEnhancementFlow',
              }
            })
          })
          .finally(() => {
            setAIProcessingStep(undefined)
          })
      } else if (generateRef.current) {
        // Fallback to the old behavior for other flows
        generateRef.current.handleMagicEraserGenerate()
      }
    }

    return runGenerate()
  }, [isLabSdkEngine, runLabErase, runLabAllFeatures, ensureTempPreviewForAITool, getVisualStagingSettings, getWallPaintingSettings, handleProcessMagicEraserWithMaskClear, hasAIEraserCompleted, hasMaskDrawn, isMagicEraserTool, isEnhanceTool, isProcessingImage, isVirtualStaging, photoStudioHistory, previewImage, processAIEnhancement, processWallPaintingWithSettings, processFloorReplacementWithTemplate, processDeclutterRoom, setAIProcessingStep, setHasEnhanceCompleted, setHasVisualStagingCompleted, setIsProcessingImage, setVirtualStagingJobId, setVirtualStagingState, spawnErrorToast, t, uploadMaskAndProcess, virtualStagingJobId, virtualStagingState, imageGenerationSequenceNumber])

  const activeSidebarOption = activeFeature

  const handleSidebarOptionSelect = useCallback(async (option: PhotoStudioSidebarOption) => {
    // Disable feature switching during image processing
    if (isProcessingImage) {
      return
    }

    if (comingSoonSidebarOptionsSet.has(option)) {
      return
    }

    // Track edition group clicked
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_EDITION_GROUP_CLICKED, {
      editionGroup: option,
      propertyType: propertyType || undefined,
    })

    try {
      await switchEditingMode(option)
    } catch (error) {
      spawnErrorToast(t('general_error', { error }))
    }
  }, [switchEditingMode, spawnErrorToast, t, isProcessingImage, logPhotoStudioEvent, propertyType, comingSoonSidebarOptionsSet])

  // Handler for cancel action that logs analytics before exiting
  const handleCancel = useCallback(() => {
    logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_CANCELLED, {
      tool: selectedAIFeature || selectedPinturaFeature || 'none',
      propertyType: propertyType || undefined,
    })
    onExitStudio()
  }, [logPhotoStudioEvent, selectedAIFeature, selectedPinturaFeature, propertyType, onExitStudio])

  return (
    <ActionPage
      id={`photo-studio-assignment-${assignmentId}-img-${sourceVisualFilename}`}
      title={t('title')}
      onClose={handleCancel}
      // customNavigationTab={<ClientPhotoStudioNavTabs />} //Temporarily disabled, will be enabled when we start work on Video Generation
      disabled={isProcessingImage || isToolSwitching}
      footerChildren={
        <ClientPhotoStudioFooter
          onClose={handleCancel}
          onSave={(tag) => {
            // Simplified save logic: use Pintura flow for manual tools, history-based save for everything else.
            // History-based save always saves exactly what's visible in preview, regardless of:
            // - Which tool is active
            // - Undo/redo state
            // - Whether a job exists
            if (selectedPinturaFeature) {
              handleUploadImage(tag, false, false)
            } else {
              // History-based save - saves the current version from history
              handleUploadImage(tag, false, true)
            }
          }}
          isProcessing={isProcessingImage}
          isSaving={isSaving}
          isToolSwitching={isToolSwitching}
          canSave={canSave}
          marginRight={4}
        />
      }
    >
      <Box sx={{ position: 'relative', height: '100%' }}>
        <div style={{ height: '100%', maxHeight: '100%', minHeight: 0, display: 'grid', gridTemplateColumns: '1fr 3fr', gridTemplateRows: '1fr', overflow: 'hidden' }}>

          {/* LEFT TOOLBAR/MENU WITH SUBSCRIPTION PANEL, AI AND MANUAL TOOLS */}
          <Box
            ref={leftColumnRef}
            aria-busy={isToolSwitching}
            sx={{
              opacity: isToolSwitching ? 0.5 : 1,
              pointerEvents: isToolSwitching ? 'none' : 'auto',
              transition: 'opacity .2s ease',
              height: '100%',
              maxHeight: '100%',
              minHeight: 0,
              width: '100%',
              maxWidth: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              position: 'relative',
              zIndex: 1,
              backgroundColor: WHITE,
            }}
          >

            {/* Lab SDK engine: One tool | Combine tools, on top of the column it changes */}
            {isAllFeaturesViewAvailable && <LabToolsViewSwitch />}

            <Stack
              direction="row"
              gap={0}
              sx={{
                flex: '1 1 auto',
                minHeight: 0,
                width: '100%',
                maxWidth: '100%',
                overflow: 'hidden',
                borderTop: `.1rem solid ${GRAY_300}`,
              }}
            >
              {/* Today's tool tiles — the all-features view lists every tool as a section instead */}
              {!isAllFeaturesView && (
                <Stack
                  spacing={0.8}
                  sx={{
                    width: LEFT_COLUMN_WIDTH,
                    minWidth: LEFT_COLUMN_WIDTH,
                    maxWidth: LEFT_COLUMN_WIDTH,
                    padding: '1.2rem',
                    backgroundColor: WHITE,
                    alignItems: 'center',
                    zIndex: 2, // Higher z-index to ensure shadow is visible above right column
                    boxShadow: '0 4px 16px 0 rgba(19, 24, 29, 0.10)', // Right side shadow (lg) - replaces border
                  }}
                >
                  {SIDEBAR_OPTIONS.filter((option) => option.id !== PhotoStudioSidebarOption.BLUR || isPinturaReplacement).map((option) => {
                    const isComingSoon = comingSoonSidebarOptionsSet.has(option.id)
                    const isActive = activeSidebarOption === option.id
                    // The Lab engine's Before view keeps the tools usable (Generate reuses that Before)
                    const isDisabled = isComingSoon || isProcessingImage || (isOriginalVisualType && !isLabSdkEngine)
                    const IconComponent = option.Icon
                    const label = t(option.labelKey)

                    return (
                      <Fragment key={option.id}>
                        {/* On the wrapper, not the button: a disabled tile still names itself on hover */}
                        <MUITooltip content={isComingSoon ? `${label} · ${t('common:coming_soon')}` : label} placement="right" arrow>
                          <Box position="relative">
                            {isComingSoon && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '-.4rem',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  pointerEvents: 'none',
                                  zIndex: 2,
                                }}
                              >
                                <MUIBadge label={t('common:coming_soon')} size="xs" color="coral" />
                              </Box>
                            )}
                            <ButtonBase
                              disableRipple
                              disabled={isDisabled}
                              aria-label={label}
                              onClick={() => {
                                if (!isDisabled) {
                                  handleSidebarOptionSelect(option.id)
                                }
                              }}
                              sx={{
                                width: SIDEBAR_TILE_WIDTH,
                                height: SIDEBAR_TILE_HEIGHT,
                                justifyContent: 'center',
                                borderRadius: '1rem',
                                padding: 0,
                                border: `0.1rem solid ${isDisabled ? GRAY_300 : (isActive ? BLUE_600 : GRAY_300)}`,
                                backgroundColor: isDisabled ? GRAY_100 : (isActive ? BLUE_100 : WHITE),
                                alignItems: 'center',
                                display: 'flex',
                                color: isDisabled ? GRAY_600 : GRAY_900,
                                cursor: isDisabled ? 'not-allowed' : 'pointer',
                                transition: 'all .2s ease',
                                '&:hover': {
                                  backgroundColor: isDisabled ? 'transparent' : BLUE_100,
                                  borderColor: isDisabled ? 'transparent' : BLUE_600,
                                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                                },
                                '& .menu-icon': {
                                  color: isDisabled ? GRAY_600 : (isActive ? GRAY_800 : GRAY_600),
                                },
                                '&:hover .menu-icon': {
                                  color: isDisabled ? GRAY_600 : GRAY_800,
                                },
                                '&.Mui-disabled': {
                                  opacity: 1,
                                  color: GRAY_600,
                                  borderColor: GRAY_300,
                                  backgroundColor: isComingSoon ? GRAY_200 : GRAY_100,
                                  cursor: 'not-allowed',
                                  pointerEvents: 'auto',
                                },
                              }}
                            >
                              <IconComponent
                                className="menu-icon"
                                sx={() => ({
                                  fontSize: '2.2rem',
                                  color: isDisabled
                                    ? GRAY_500
                                    : isActive
                                      ? GRAY_800
                                      : GRAY_600,
                                })}
                              />
                            </ButtonBase>
                          </Box>
                        </MUITooltip>
                        {option.hasDividerAfter && (
                          <Divider
                            sx={{
                              borderColor: GRAY_300,
                              width: '80%',
                              alignSelf: 'center',
                            }}
                          />
                        )}
                      </Fragment>
                    )
                  })}
                </Stack>
              )}

              <Box
                sx={{
                  width: isAllFeaturesView ? TOTAL_PANEL_WIDTH : RIGHT_COLUMN_WIDTH,
                  minWidth: isAllFeaturesView ? TOTAL_PANEL_WIDTH : RIGHT_COLUMN_WIDTH,
                  maxWidth: isAllFeaturesView ? TOTAL_PANEL_WIDTH : RIGHT_COLUMN_WIDTH,
                  height: '100%',
                  maxHeight: '100%',
                  borderLeft: isAllFeaturesView ? 'none' : `.1rem solid ${GRAY_300}`,
                  backgroundColor: WHITE,
                  // No padding for AI tools because they have generate button in the menu — nor for Adjust locked on the Lab
                  // engine, which has the Subscribe one in the same place — nor for the in-house editor's Adjust and Blur
                  // panels, which scroll with all of it inside: a scroll container clips at its own edge, and a slider
                  // thumb at its minimum sits half outside the track
                  padding: isAllFeaturesView || isVirtualStaging || isMagicEraserTool || isEnhanceTool || (isAdjustTool && isLabSdkEngine && !hasWorkspaceActiveSubscription) || (isAdjustTool && isPinturaReplacement && hasWorkspaceActiveSubscription) ? 0 : '1.6rem',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0, // Allow flex children to shrink below content size
                  position: 'relative',
                  zIndex: 0, // Lower z-index so left column's shadow is visible
                }}
              >
                {isAllFeaturesView && (
                  <LabAllFeaturesPanel
                    isDisabled={isProcessingImage || isSaving}
                    isProcessingImage={isProcessingImage}
                    canErase={canGenerateMagicEraser}
                    onGenerate={handleLabAllFeaturesGenerate}
                  />
                )}

                {!isAllFeaturesView && (
                  <>
                    {isAdjustTool && !isBlurTool && <AdjustPanel />}

                    {isBlurTool && <BlurPanel />}

                    {isVirtualStaging && isLabSdkEngine && (
                      <LabStagePanel
                        isDisabled={isProcessingImage || isSaving}
                        isProcessingImage={isProcessingImage}
                        onGenerate={handleLabStageGenerate}
                      />
                    )}

                    {isVirtualStaging && !isLabSdkEngine && (
                      <VirtualStagingPanelContainer
                        virtualStagingState={virtualStagingState}
                        setVirtualStagingState={setVirtualStagingState}
                        onGenerate={() => handleGenerate()}
                        generateRef={generateRef}
                        hasMask={hasMaskDrawn}
                        isMaskRequired={isStagingMaskRequired}
                        canGenerate={canGenerateVirtualStaging}
                      />
                    )}

                    {isMagicEraserTool && (
                      <MagicEraserPanel
                        isDisabled={isProcessingImage || isSaving || (isOriginalVisualType && !isLabSdkEngine)}
                        onGenerate={() => {
                          // Magic Eraser always calls the AI action fresh
                          if (generateRef.current) {
                            generateRef.current.handleMagicEraserGenerate()
                          }
                        }}
                        hasAIEraserCompleted={hasAIEraserCompleted}
                        isProcessingImage={isProcessingImage}
                        hasMask={hasMaskDrawn}
                        canGenerate={canGenerateMagicEraser}
                      />
                    )}

                    {isEnhanceTool && isLabSdkEngine && (
                      <LabEnhancePanel
                        isDisabled={isProcessingImage || isSaving}
                        isProcessingImage={isProcessingImage}
                        onGenerate={handleLabEnhanceGenerate}
                      />
                    )}

                    {isEnhanceTool && !isLabSdkEngine && <EnhancePanel isDisabled={isProcessingImage || isSaving || isOriginalVisualType} isProcessingImage={isProcessingImage} onGenerate={(skyReplacement) => handleGenerate(undefined, skyReplacement)} />}
                  </>
                )}
              </Box>

            </Stack>
          </Box>

          {/* RIGHT PREVIEWS FOR VISUAL EDITING — one grid cell, so the tool-switch overlay covers exactly the photo side */}
          <Box sx={{ position: 'relative', height: '100%', maxHeight: '100%', minHeight: 0, minWidth: 0, overflow: 'hidden' }}>

            {!isAdjustTool && (
              <Box sx={{ height: '100%', maxHeight: '100%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <UnifiedPreview
                  ref={unifiedPreviewRef}
                  imageUrl={convertedImageUrl}
                  previewImage={previewImage}
                  hasWorkspaceActiveSubscription={hasWorkspaceActiveSubscription}
                  isProcessingImage={isProcessingImage}
                  isSaving={isSaving}
                  currentFlow={currentFlow}
                  selectedPinturaFeature={selectedPinturaFeature}
                  virtualStagingState={virtualStagingState}
                  onVisualStagingStyleChange={changeSelectedStyle}
                  isMagicEraserTool={isMagicEraserTool}
                  hasAIEraserCompleted={hasAIEraserCompleted}
                  clearMaskTrigger={clearMaskTrigger}
                  onMaskChange={handleMaskChange}
                  onPreviewImageChange={handlePreviewImageChange}
                  onGenerate={handleGenerate}
                  generateRef={generateRef}
                  logPhotoStudioEvent={logPhotoStudioEvent}
                  assignmentId={assignmentId}
                  sourceVisualFilename={sourceVisualFilename}
                />
              </Box>
            )}

            {isAdjustTool && !isPinturaReplacement && <ClientPhotoStudioManualToolsPreview />}

            {isAdjustTool && isPinturaReplacement && <AdjustStage />}

            {/* Tool switch (e.g. leaving Adjust): over the photo only; the tools column is disabled meanwhile */}
            {isToolSwitching && (
              <PhotoStudioSaving
                width="100%"
                height="100%"
                showBlurOverlay={true}
                showSpinner={true}
                step={toolSwitchStep}
                progress={toolSwitchProgress}
              />
            )}

          </Box>

        </div>
      </Box>

    </ActionPage>
  )
}
